import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { ROUTES, USER_ROLES, PERMISSIONS } from '@/utils/constants'

// Layout Components
import Layout from '@/components/layout/Layout'
import ProtectedRoute from '@/components/auth/ProtectedRoute'

// Auth Pages
import LoginForm from '@/components/auth/LoginForm'

// Main Pages
import Dashboard from '@/pages/dashboard/Dashboard'
import Profile from '@/pages/auth/Profile'
import EditProfile from '@/pages/auth/EditProfile'

// Brand Pages
import BrandList from '@/pages/brands/BrandList'
import BrandDetail from '@/pages/brands/BrandDetail'
import BrandCreate from '@/pages/brands/BrandCreate'
import BrandEdit from '@/pages/brands/BrandEdit'

// Data Input Pages
import DataInputList from '@/pages/data-inputs/DataInputList'
import DataInputDetail from '@/pages/data-inputs/DataInputDetail'
import DataInputCreate from '@/pages/data-inputs/DataInputCreate'
import DataInputEdit from '@/pages/data-inputs/DataInputEdit'

// Attribute Pages
import AttributeDetail from '@/pages/data-inputs/attributes/AttributeDetail'
import AttributeCreate from '@/pages/data-inputs/attributes/AttributeCreate'
import AttributeEdit from '@/pages/data-inputs/attributes/AttributeEdit'

// Record Pages
import RecordCreate from '@/pages/data-inputs/records/RecordCreate'
import RecordEdit from '@/pages/data-inputs/records/RecordEdit'
import BulkUpload from '@/pages/data-inputs/records/BulkUpload'

// Area Pages (Admin and Manager)
import { AreaList, AreaCreate, AreaDetails, AreaEdit } from '@/pages/areas'

// User Pages (Admin only)
import UserList from '@/pages/users/UserList'
import UserDetail from '@/pages/users/UserDetail'
import UserCreate from '@/pages/users/UserCreate'
import UserEdit from '@/pages/users/UserEdit'

// Developer Pages
import DeveloperPermissions from '@/pages/developer/DeveloperPermissions'

// File Pages
import FileManager from '@/pages/files/FileManager'

// Settings Pages
import { Settings } from '@/pages/settings'

// Audit Pages
import AuditLogs from '@/pages/audit/AuditLogs'

// Error Pages
import NotFound from '@/pages/errors/NotFound'

function App() {
  const { isAuthenticated } = useAuth()

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route 
          path={ROUTES.LOGIN} 
          element={
            isAuthenticated ? (
              <Navigate to={ROUTES.DASHBOARD} replace />
            ) : (
              <LoginForm />
            )
          } 
        />


        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  {/* Dashboard */}
                  <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                  <Route path={ROUTES.PROFILE} element={<Profile />} />
                  <Route path={ROUTES.EDIT_PROFILE} element={<EditProfile />} />

                  {/* Brands */}
                  <Route path={ROUTES.BRANDS} element={<BrandList />} />
                  <Route path={ROUTES.BRAND_DETAIL} element={<BrandDetail />} />
                  <Route 
                    path={ROUTES.BRAND_CREATE} 
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.CREATE}>
                        <BrandCreate />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path={ROUTES.BRAND_EDIT}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <BrandEdit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Data Inputs */}
                  <Route path={ROUTES.DATA_INPUTS} element={<DataInputList />} />
                  <Route path={ROUTES.DATA_INPUT_DETAIL} element={<DataInputDetail />} />
                  <Route
                    path={ROUTES.DATA_INPUT_CREATE}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.CREATE}>
                        <DataInputCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.DATA_INPUT_EDIT}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <DataInputEdit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Data Input Attributes */}
                  <Route path={ROUTES.ATTRIBUTE_DETAIL} element={<AttributeDetail />} />
                  <Route
                    path={ROUTES.ATTRIBUTE_CREATE}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.CREATE}>
                        <AttributeCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.ATTRIBUTE_EDIT}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.EDIT}>
                        <AttributeEdit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Data Input Records - Order matters! More specific routes first */}
                  <Route
                    path={ROUTES.RECORD_CREATE}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.VIEW}>
                        <RecordCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECORD_EDIT}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.EDIT}>
                        <RecordEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECORD_BULK_UPLOAD}
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.VIEW}>
                        <BulkUpload />
                      </ProtectedRoute>
                    }
                  />


                  {/* Settings (Consolidated Brands, Areas, Users) */}
                  <Route path={ROUTES.SETTINGS} element={<Settings />} />

                  {/* Audit Logs */}
                  <Route
                    path={ROUTES.AUDIT_LOGS}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <AuditLogs />
                      </ProtectedRoute>
                    }
                  />

                  {/* File Manager */}
                  <Route
                    path="/files"
                    element={
                      <ProtectedRoute>
                        <FileManager />
                      </ProtectedRoute>
                    }
                  />

                  {/* Legacy Routes - Redirect to Settings with appropriate tab */}
                  <Route path={ROUTES.BRANDS} element={<Settings />} />

                  {/* Areas */}
                  <Route
                    path={ROUTES.AREAS}
                    element={<Settings />}
                  />
                  <Route
                    path={ROUTES.AREA_DETAIL}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.AREA_MANAGER, USER_ROLES.USER]}>
                        <AreaDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.AREA_CREATE}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <AreaCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.AREA_EDIT}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <AreaEdit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Users */}
                  <Route
                    path={ROUTES.USERS}
                    element={<Settings />}
                  />
                  <Route
                    path={ROUTES.USER_DETAIL}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <UserDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_CREATE}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <UserCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_EDIT}
                    element={
                      <ProtectedRoute requiredRole={[USER_ROLES.ADMIN, USER_ROLES.MANAGER]}>
                        <UserEdit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Developer (Admin only) */}
                  <Route
                    path={ROUTES.DEVELOPER_PERMISSIONS}
                    element={
                      <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                        <DeveloperPermissions />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirect - only redirect root path to dashboard */}
                  <Route
                    path="/"
                    element={<Navigate to={ROUTES.DASHBOARD} replace />}
                  />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  )
}

export default App
